import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { showModal } from 'utils/modal';
import { getMovieStatus } from 'utils/movieStatus';
import { getCurrentMovieUrl, isBaseRegion } from 'utils/studioConfig';
import { joinClasses } from 'utils/utils';

import Badge from 'components/badge';
import Button from 'components/button/button';
import RegionSelector from 'components/regionSelector/regionSelector';
import PageSettings from 'components/pageSettings/pageSettings';
import Toggle from 'components/toggle/toggle';

import EditorSidebar from 'components/editor/sidebar';

import s from 'components/editor/movies/movieSidebar/movieSidebar.sss';

export default class MovieSidebar extends EditorSidebar {
	s = s;

	get title() {
		const movie = this.state.movieData;
		const baseTitle = movie?.base_movie?.content?.title;
		const localeTitle = movie?.content?.title;
		return baseTitle || localeTitle || 'Untitled Movie';
	}

	get url() {
		if (!this.isLive) return null;

		// First check if we have published data for the movie
		const publishedData = this.state.publishedData;
		if (publishedData?.url) return publishedData?.url;
		if (publishedData?.slug) return getCurrentMovieUrl(publishedData?.slug);

		// If no published data is available, fall back to the movie's content data
		const movie = this.state.movieData?.content;
		if (movie?.url) return movie?.url;
		if (movie?.slug) return getCurrentMovieUrl(movie.slug);

		return null;
	}

	constructor(props) {
		super(props);

		this.updateMovieData();

		this.togglePageSection = this.togglePageSection.bind(this);
	}

	componentDidUpdate(prevProps) {
		super.componentDidUpdate?.(prevProps);

		const prevData = jsonPointer.get(prevProps?.data, `${prevProps?.activeSection?.dataRoot}`);
		if (prevData !== this.data) {
			this.updateMovieData();
		}
	}

	async updateMovieData() {
		const movie = this.data;
		const status = await getMovieStatus(movie);

		const movieData = {
			...movie,
			status,
		};

		this.setState({ movieData });

		// Load published data
		await Store.get().publishedDataPromise;
		const publishedData = Store.get().publishedData?.movies?.[movie?.id];
		this.setState({ publishedData });
	}

	getMovieStatuses(movie = this.state.movieData) {
		const status = movie?.status || {};
		return [
			status.theaters === 'inTheaters' && 'In Theaters',
			status.theaters === 'preSale' && 'Pre-sale',
			status.theaters === 'comingSoon' && 'Coming Soon',
			status.homeEnt === 'homeEnt' && 'At Home',
		].filter(Boolean);
	}

	renderTitle() {
		const movie = this.state.movieData;
		// If the movie has a base movie, we want to show the localized title
		const localeTitle = movie?.base_movie ? (movie?.content?.title || this.title) : null;
		const baseMovieId = movie?.base_movie_id || (isBaseRegion() && movie?.id) || null;
		return (
			<div class={this.s.title}>
				<h2>{this.title}</h2>
				{localeTitle && <span class={this.s.localeTitle}>{localeTitle}</span>}
				{baseMovieId && <RegionSelector dark class={this.s.regionSelector} movie={baseMovieId} />}
			</div>
		);
	}

	renderSubHeading() {}

	renderPageStatus() {
		if (isBaseRegion()) return null;

		const isLive = !!this.data?.commits?.publish;
		const isPlaceholder = this.data?.published_status?.endsWith('_PLACEHOLDER');
		const pageType = isPlaceholder ? 'Placeholder' : 'Movie platform';

		const { current, publish } = this.data?.commits || {};
		const unpublishedChanges = current?.at > publish?.at;

		return (
			<div class={this.s.pageStatus}>
				<span>{pageType}</span>
				{this.renderBadge(isLive ? 'live' : 'draft', unpublishedChanges)}
			</div>
		);
	}

	togglePageSection(e, section, page, ps) {
		const active = !!e.target.checked;
		jsonPointer.setDeep(Store.get().data, `${section.dataRoot || ''}/content/pages/${ps.id}/active`, active);
	}

	openPageSettings({ type, sectionRoot, page, dataRoot }) {
		showModal(PageSettings, {
			type,
			page,
			dataRoot,
			sectionRoot,
		});
	}

	getDefaultMoviePageId(section) {
		const { dataRoot } = section;
		const pages = jsonPointer.get(this.props.data, `${dataRoot}/content/pages`) || {};
		// Find the page with default settings or fallback to the home page
		return Object.entries(pages).find(([, page]) => page?.settings?.default)?.[0] || 'home';
	}

	renderSubPages(section, page, subPage) {
		const isActive = jsonPointer.get(
			this.props.data,
			`${section.dataRoot || ''}/content/pages/${subPage.id}/active`
		);
		const pageDataRoot = jsonPointer.concat(section.dataRoot, '/content/pages/', subPage.id);
		return (
			<li key={subPage.id}>
				<div class={this.s.pageName}>
					<Toggle small checked={isActive} onInput={(e) => this.togglePageSection(e, section, page, subPage)}>
						{subPage.name}
					</Toggle>
					{!(page.disableSettings || section.disableSettings) && (
						<Button
							class={this.s.pageSettings}
							icon="settings"
							iconSize="small"
							onClick={() => this.openPageSettings({ type: 'subPage', sectionRoot: section.dataRoot, dataRoot: pageDataRoot, page: subPage.id })}
						/>
					)}
				</div>
			</li>
		);
	}

	renderPage(section, page, isSectionActive, isDefault) {
		const activePagePath = isSectionActive && this.props.path[3];

		const pageSections = page.pageSections;
		const pageDataRoot = jsonPointer.concat(section.dataRoot, '/content/pages', page.dataRoot);
		const pageData = jsonPointer.get(
			this.props.data,
			pageDataRoot
		);

		return (
			<li class={joinClasses(this.s.page, isDefault && this.s.landing, activePagePath === page.path && this.s.activePage)} key={page.path}>
				<div class={this.s.pageName}>
					<a href={`#${this.props.config.basePath}/${section.path}/${page.path}`}>{page.name}</a>
					<div class={this.s.badges}>
						{page.badges?.filter(badge => !badge.path || jsonPointer.get(pageData, badge.path))?.map(badge => (
							<Badge dark type={badge.type} />
						))}
					</div>
					{!(page.disableSettings || section.disableSettings) && (
						<Button
							class={this.s.pageSettings}
							icon="settings"
							iconSize="small"
							onClick={() => this.openPageSettings({ type: 'page', sectionRoot: section.dataRoot, dataRoot: pageDataRoot, page: page.path })}
						/>
					)}
				</div>
				{!!pageSections?.length && (
					<ul class={this.s.pageSections}>
						{pageSections.map((ps) => this.renderSubPages(section, page, ps))}
					</ul>
				)}
			</li>
		);
	}

	renderSectionPages(section) {
		const activeSection = this.props.activeSection;
		const isActive = section === activeSection;
		const defaultPage = this.getDefaultMoviePageId(section);

		return (
			<div class={joinClasses(this.s.section, this.s.pagesSection, isActive && this.s.active)}>
				<div class={this.s.sectionContent}>
					{section.name}
					<ul class={this.s.pages}>
						{section.pages.map((page) => this.renderPage(section, page, isActive, page.dataRoot === `/${defaultPage}`))}
					</ul>
				</div>
			</div>
		);
	}

	renderSection(section) {
		if (section.path === 'pages') {
			return this.renderSectionPages(section);
		}
		return super.renderSection(section);
	}

	renderContent() {
		// Loading state for which regions of the movie
		if (this.props.loading) {
			return (
				<div class={this.s.inner}>
					<div class={this.s.header}>
						{this.renderTitle()}
					</div>
				</div>
			);
		}

		return super.renderContent();
	}
}
